import { useEffect, useState, type FormEvent } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { createPractitioner, updatePractitioner } from "@/api/practitioners";
import {
  type Skill,
  listPractitionerSkills,
  listSkills,
  setPractitionerSkills,
} from "@/api/skills";
import type { Practitioner } from "@/api/types";
import { usePermission } from "@/auth/usePermission";

type Props = {
  open: boolean;
  practitioner: Practitioner | null;
  onClose: () => void;
  onSaved: (saved: Practitioner) => void;
};

export function PractitionerFormDrawer({ open, practitioner, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = Boolean(practitioner);

  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [firstInitial, setFirstInitial] = useState("");
  const [rpps, setRpps] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [annualLeaveDays, setAnnualLeaveDays] = useState(25);
  const [serverError, setServerError] = useState<string | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);

  const canReadSkills = usePermission("skill:read");

  const skillsQuery = useQuery({
    queryKey: ["skills", "active"],
    queryFn: () => listSkills(true),
    enabled: open && canReadSkills,
  });

  const practSkillsQuery = useQuery({
    queryKey: ["practitioner-skills", practitioner?.id],
    queryFn: () =>
      practitioner ? listPractitionerSkills(practitioner.id) : Promise.resolve([]),
    enabled: open && Boolean(practitioner) && canReadSkills,
  });

  useEffect(() => {
    if (practSkillsQuery.data) {
      setSelectedSkills(practSkillsQuery.data);
    } else if (open && !practitioner) {
      setSelectedSkills([]);
    }
  }, [practSkillsQuery.data, open, practitioner]);

  useEffect(() => {
    if (open) {
      setLastName(practitioner?.last_name ?? "");
      setFirstName(practitioner?.first_name ?? "");
      setFirstInitial(practitioner?.first_initial ?? "");
      setRpps(practitioner?.rpps ?? "");
      setEmail(practitioner?.email ?? "");
      setPhone(practitioner?.phone ?? "");
      setAnnualLeaveDays(practitioner?.annual_leave_days ?? 25);
      setServerError(null);
    }
  }, [open, practitioner]);

  const mutation = useMutation({
    mutationFn: async () => {
      const basePayload = {
        last_name: lastName,
        first_name: firstName || null,
        first_initial: firstInitial || null,
        rpps: rpps || null,
        email: email || null,
        phone: phone || null,
      };
      const saved =
        isEdit && practitioner
          ? await updatePractitioner(practitioner.id, {
              ...basePayload,
              annual_leave_days: annualLeaveDays,
            })
          : await createPractitioner(basePayload);
      if (canReadSkills) {
        await setPractitionerSkills(saved.id, selectedSkills.map((s) => s.id));
      }
      return saved;
    },
    onSuccess: (p) => onSaved(p),
    onError: (e: unknown) => {
      const detail =
        (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      if (detail && typeof detail === "object" && (detail as { error?: string }).error === "duplicate_rpps") {
        setServerError(t("practitioners.duplicateRppsMessage"));
      } else {
        setServerError(typeof detail === "string" ? detail : (e as Error).message);
      }
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 480 } } },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEdit ? t("practitioners.edit") : t("practitioners.create")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box component="form" onSubmit={onSubmit} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Stack spacing={2}>
            {serverError && <Alert severity="error">{serverError}</Alert>}
            <TextField
              label={t("practitioners.lastName")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label={t("practitioners.firstName")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
              />
              <TextField
                label={t("practitioners.firstInitial")}
                value={firstInitial}
                onChange={(e) => setFirstInitial(e.target.value)}
                sx={{ maxWidth: { sm: 120 } }}
                inputProps={{ maxLength: 8 }}
              />
            </Stack>
            <TextField
              label={t("practitioners.rpps")}
              value={rpps}
              onChange={(e) => setRpps(e.target.value)}
              helperText={t("practitioners.rppsHint")}
              inputProps={{ maxLength: 11, inputMode: "numeric", pattern: "[0-9]*" }}
              fullWidth
            />
            <TextField
              label={t("practitioners.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              fullWidth
            />
            <TextField
              label={t("practitioners.phone")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
            />
            {isEdit && (
              <TextField
                label={t("practitioners.annualLeaveDays")}
                type="number"
                value={annualLeaveDays}
                onChange={(e) =>
                  setAnnualLeaveDays(Math.max(0, Math.min(365, Number(e.target.value) || 0)))
                }
                inputProps={{ min: 0, max: 365 }}
                helperText={t("practitioners.annualLeaveDaysHelp")}
                fullWidth
              />
            )}
            {canReadSkills && (
              <Autocomplete
                multiple
                options={skillsQuery.data ?? []}
                value={selectedSkills}
                onChange={(_, v) => setSelectedSkills(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.label}
                        size="small"
                        sx={{
                          bgcolor: option.color ?? undefined,
                          color: option.color ? "#fff" : undefined,
                        }}
                        {...chipProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label={t("skills.formLabel")} placeholder={t("skills.formPlaceholder")} />
                )}
              />
            )}
          </Stack>

          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 2 }}>
            <Button onClick={onClose} disabled={mutation.isPending}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending || !lastName}
            >
              {t("common.save")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
}
